import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type {
  Space,
  SpaceCreate,
  SpaceUpdate,
  SpaceListResponse,
  SpaceSection,
  SpaceMember,
  SpaceOutputSkin,
} from "@/types/space";

export const spacesApi = {
  async list(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    categoryId?: number;
    search?: string;
  }): Promise<SpaceListResponse> {
    const { data } = await apiClient.get("/spaces", {
      params: {
        page: params?.page || 1,
        page_size: params?.pageSize || 20,
        status: params?.status,
        category_id: params?.categoryId,
        search: params?.search,
      },
    });
    return toCamelCase(data) as SpaceListResponse;
  },

  async get(id: number): Promise<Space> {
    const { data } = await apiClient.get(`/spaces/${id}`);
    return toCamelCase(data) as Space;
  },

  async create(space: SpaceCreate): Promise<Space> {
    const { data } = await apiClient.post("/spaces", toSnakeCase(space));
    return toCamelCase(data) as Space;
  },

  async update(id: number, space: SpaceUpdate): Promise<Space> {
    const { data } = await apiClient.put(`/spaces/${id}`, toSnakeCase(space));
    return toCamelCase(data) as Space;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/spaces/${id}`);
  },

  async getSections(id: number): Promise<SpaceSection[]> {
    const { data } = await apiClient.get(`/spaces/${id}/sections`);
    return (toCamelCase(data) as SpaceSection[]);
  },

  async getFicha(id: number): Promise<Record<string, string>> {
    const { data } = await apiClient.get(`/spaces/${id}/ficha`);
    const camel = toCamelCase(data) as { ficha?: Record<string, string> };
    return camel.ficha || {};
  },

  async updateFicha(
    id: number,
    ficha: Record<string, string>,
    options?: { merge?: boolean },
  ): Promise<Record<string, string>> {
    const merge = options?.merge !== false;
    const { data } = await apiClient.put(
      `/spaces/${id}/ficha`,
      { ficha },
      { params: { merge } },
    );
    const camel = toCamelCase(data) as { ficha?: Record<string, string> };
    return camel.ficha || {};
  },

  // ─── Miembros del espacio ──────────────────────────────────
  async getMembers(id: number): Promise<SpaceMember[]> {
    const { data } = await apiClient.get(`/spaces/${id}/members`);
    const camel = toCamelCase(data) as { members: SpaceMember[] };
    return camel.members || [];
  },

  async addCollaborator(id: number, email: string): Promise<SpaceMember[]> {
    const { data } = await apiClient.post(`/spaces/${id}/collaborators`, { email });
    const camel = toCamelCase(data) as { members: SpaceMember[] };
    return camel.members || [];
  },

  async removeCollaborator(id: number, userId: number): Promise<SpaceMember[]> {
    const { data } = await apiClient.delete(`/spaces/${id}/collaborators/${userId}`);
    const camel = toCamelCase(data) as { members: SpaceMember[] };
    return camel.members || [];
  },

  // ─── Plantillas de salida (skins) ──────────────────────────────────
  async getOutputSkins(id: number): Promise<SpaceOutputSkin[]> {
    const { data } = await apiClient.get(`/spaces/${id}/output-skins`);
    return (toCamelCase(data) as SpaceOutputSkin[]) || [];
  },
};
